import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Building2, Clock } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const buildLocalizedPath = (lang, subpath) => `/${lang}${subpath}`;

export const Footer = ({ onOpenCookieSettings }) => {
  const { t, lang, dict } = useI18n();
  const corporate = dict.footer.corporate;
  const address = dict.footer.address;

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#071426] text-slate-300 border-t border-slate-800"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="flex h-8 w-8 items-center justify-center bg-emerald-600 text-white rounded-sm">
              <Building2 className="h-4 w-4" />
            </span>
            <span className="font-semibold tracking-[0.18em] text-white text-sm">
              {t("header.brand")}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-slate-400">
            {t("footer.company_description")}
          </p>
          <div className="mt-5 h-1 w-12 bg-emerald-600" aria-hidden="true" />
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
            {t("footer.sections.legal")}
          </h4>
          <ul className="space-y-2 text-sm">
            <li>
              <Link
                to={buildLocalizedPath(lang, "/privacy-policy")}
                data-testid="footer-link-privacy"
                className="hover:text-emerald-300"
              >
                {t("footer.legal_links.privacy")}
              </Link>
            </li>
            <li>
              <Link
                to={buildLocalizedPath(lang, "/cookie-policy")}
                data-testid="footer-link-cookies"
                className="hover:text-emerald-300"
              >
                {t("footer.legal_links.cookies")}
              </Link>
            </li>
            <li>
              <Link
                to={buildLocalizedPath(lang, "/terms")}
                data-testid="footer-link-terms"
                className="hover:text-emerald-300"
              >
                {t("footer.legal_links.terms")}
              </Link>
            </li>
            <li>
              <button
                type="button"
                data-testid="footer-cookie-settings"
                onClick={onOpenCookieSettings}
                className="hover:text-emerald-300 text-left"
              >
                {lang === "fr" ? "Paramètres des cookies" : "Cookie settings"}
              </button>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
            {t("footer.sections.company")}
          </h4>
          <dl className="space-y-2 text-xs text-slate-400">
            <div>
              <dt className="text-slate-500">{corporate.form}</dt>
              <dd className="text-slate-200 font-medium">{corporate.name}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{corporate.siren_label}</dt>
              <dd>{corporate.siren_value}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{corporate.siret_label}</dt>
              <dd>{corporate.siret_value}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{corporate.tva_label}</dt>
              <dd>{corporate.tva_value}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{corporate.ape_label}</dt>
              <dd>{corporate.ape_value}</dd>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-slate-500">{corporate.capital_label}</dt>
                <dd>{corporate.capital_value}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{corporate.incorporation_label}</dt>
                <dd>{corporate.incorporation_value}</dd>
              </div>
            </div>
            <div>
              <dt className="text-slate-500">{corporate.rcs_label}</dt>
              <dd>{corporate.rcs_value}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{corporate.jurisdiction_label}</dt>
              <dd>{corporate.jurisdiction_value}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{corporate.director_label}</dt>
              <dd>{corporate.director_value}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{corporate.amf_label}</dt>
              <dd className="text-slate-300 leading-relaxed">{corporate.amf_value}</dd>
            </div>
          </dl>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
            {t("footer.sections.contact")}
          </h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-emerald-400" aria-hidden="true" />
              <span>
                {address.line1}
                <br />
                {address.line2}
                <br />
                {address.country}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 text-emerald-400" aria-hidden="true" />
              <a
                href={`mailto:${address.email_value}`}
                data-testid="footer-email-link"
                className="hover:text-emerald-300"
              >
                {address.email_value}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Phone className="h-4 w-4 mt-0.5 text-emerald-400" aria-hidden="true" />
              <a
                href={`tel:${address.phone_value.replace(/\s+/g, "")}`}
                data-testid="footer-phone-link"
                className="hover:text-emerald-300"
              >
                {address.phone_value}
              </a>
            </li>
            <li className="flex items-start gap-2 text-xs text-slate-400">
              <Clock className="h-4 w-4 mt-0.5 text-emerald-400" aria-hidden="true" />
              <span>
                <span className="block text-slate-500">{t("footer.hours_label")}</span>
                {t("footer.hours_value")}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-slate-500">{t("footer.copyright")}</p>
          <p className="text-[11px] leading-relaxed text-slate-500 max-w-3xl">
            {t("footer.disclaimer")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
